// Типизированные сервисные функции для /api/operator/*.
// Все денежные суммы — в копейках (бэкенд хранит int). Фронт форматирует
// через formatKop/kopToRub при отображении.

import { api, apiDelete, apiGet, apiPatch, apiPost, apiPut } from './api';

// ─── Базовые типы ─────────────────────────────────────────────────────

export type SubscriptionStatusKey = 'trial' | 'active' | 'past_due' | 'cancelled';
/** Статус точки в списке/сети: как у подписки, либо `none` — своей подписки ещё нет. */
export type TenantStatusKey = SubscriptionStatusKey | 'none';
export type SubscriptionStatusUpper = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
export type DeviceKindUpper = 'IPAD' | 'IPHONE' | 'WEB' | 'OTHER';
export type TicketStatusUpper = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';
export type TicketChannelUpper = 'EMAIL' | 'WHATSAPP' | 'FORM' | 'PHONE';
export type PaymentStatusUpper = 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED';

// ─── Dashboard ───────────────────────────────────────────────────────

export interface Kpis {
  // ── снимок «сейчас» (не зависит от диапазона) ──
  totalTenants: number;
  active: number;
  trial: number;
  pastDue: number;
  cancelled: number;
  devicesOnline: number;
  mrr: number;            // копейки
  ordersTotal: number;
  ordersActive: number;
  usersTotal: number;
  devicesTotal: number;
  openShifts: number;
  dishesTotal: number;
  // ── за период [from, to] ──
  newTenants: number;        // новые арендаторы за диапазон
  ordersInRange: number;     // заказы за диапазон
  gmvInRange: number;        // копейки, оборот заведений за диапазон
  revenueInRange: number;    // копейки, выручка платформы за диапазон
  avgPaymentInRange: number; // копейки, средний платёж за диапазон
}

export interface DashboardTenant {
  id: string;
  name: string;
  city: string | null;
  createdAt: string;
  status: SubscriptionStatusKey;
  ownerEmail: string | null;
  ownerName: string | null;
}

export interface DashboardTicket {
  id: string;
  subject: string;
  channel: TicketChannelUpper;
  status: TicketStatusUpper;
  lastMessageAt: string;
  tenantId: string | null;
  tenantName: string | null;
}

export interface DashboardResponse {
  range: { from: string; to: string }; // ISO-границы «периодных» метрик
  kpis: Kpis;
  latestTenants: DashboardTenant[];
  openTickets: DashboardTicket[];
}

export interface DashboardQuery {
  from?: string; // ISO; по умолчанию — последние 30 дней (задаётся бэкендом)
  to?: string;
}

export const getDashboard = (q: DashboardQuery = {}) => {
  const p = new URLSearchParams();
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  const qs = p.toString();
  return apiGet<DashboardResponse>(`/dashboard${qs ? `?${qs}` : ''}`);
};

// ─── Tenants ─────────────────────────────────────────────────────────

/** Состояние доступа: ok — срок идёт, grace — просрочен, но ещё работает, blocked — закрыт. */
export type AccessState = 'ok' | 'grace' | 'blocked';

/**
 * Фильтр списка по сроку доступа: expiring — осталось ≤ 3 дн., due — оплата
 * в ближайшие 7 дн., grace — просрочен, касса ещё работает, blocked — закрыт.
 */
export type AccessFilterKey = 'all' | 'expiring' | 'due' | 'grace' | 'blocked';
export type AccessCounts = Record<Exclude<AccessFilterKey, 'all'>, number>;

/** Лимит тарифа: plan — по тарифу, extra — докуплено модулями, total = plan + extra (null — безлимит). */
export interface LimitInfo {
  plan: number | null;
  extra: number;
  total: number | null;
  used: number;
}

export interface TenantListItem {
  id: string;
  name: string;
  city: string | null;
  ownerId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
  /** Реферальный менеджер — привязку видно и меняем прямо в списке. */
  referrer: { id: string; name: string; code: string } | null;
  createdAt: string;
  /** Статус СОБСТВЕННОЙ подписки точки; `none` — подписки ещё нет. */
  status: TenantStatusKey;
  monthlyAmount: number | null; // копейки; null — своей подписки нет
  nextBillingAt: string | null;
  /**
   * Сколько точек у этого владельца (1 — обычный арендатор). Справка: у каждой
   * точки своя подписка, оплачиваются они отдельно.
   */
  networkSize: number;
  trialEndsAt: string | null;
  /** Конец доступа (триал или оплаченный период) — посчитан бэкендом. */
  accessEndsAt: string | null;
  daysLeft: number | null;
  accessState: AccessState | null;
  graceDaysLeft: number;
  /** Карта ЮKassa сохранена — продлится автосписанием. */
  autoRenew: boolean;
  ordersLast30d: number;
  revenueLast30d: number; // копейки
  devicesOnline: number;
  devicesTotal: number;
}

export interface TenantDetail {
  id: string;
  name: string;
  city: string | null;
  phone: string | null;
  domain: string | null;
  timezone: string | null;
  currency: string | null;
  createdAt: string;
  ownerId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
  /** Когда оператор последний раз завершал сессии владельца. */
  ownerSessionsRevokedAt: string | null;
  /** Куда платят гости точки: при передаче точки это реквизиты продавца. */
  guestPayments: { yukassa: boolean; transfer: boolean; qr: boolean };
  referrer: {
    id: string;
    name: string;
    code: string;
    email: string;
    attachedAt: string | null;
    commissionBps: number | null;
    source: string | null;
  } | null;
  /**
   * Сеть владельца (справка): все его точки с их собственными подписками.
   * Общей подписки у сети нет — каждая точка оплачивается отдельно.
   */
  network: {
    ownerEstablishmentCount: number;
    establishments: Array<{
      id: string;
      name: string;
      status: TenantStatusKey;
      accessEndsAt: string | null;
      daysLeft: number | null;
      accessState: AccessState | null;
    }>;
  };
  /** Собственная подписка точки; null — ещё не заведена. */
  subscription: {
    status: SubscriptionStatusKey;
    plan: string | null;
    planId: string | null;
    billingPeriod: 'MONTHLY' | 'YEARLY';
    additionalEstablishments: number;
    amount: number;
    currency: string | null;
    trialEndsAt: string | null;
    currentPeriodStart: string;
    currentPeriodEnd: string | null;
    cancelledAt: string | null;
    accessEndsAt: string | null;
    daysLeft: number | null;
    accessState: AccessState;
    graceDaysLeft: number;
    /** Карта ЮKassa сохранена: в конце срока спишется автоплатёж. */
    autoRenew: boolean;
    /** Карта сохранена (даже если автопродление отменено). */
    savedCard: boolean;
    planDetails: {
      code: string;
      name: string;
      priceMonthKopecks: number;
      priceYearKopecks: number;
      maxEstablishments: number | null;
      maxCashiers: number | null;
      maxUsers: number | null;
      extraEstablishmentPriceMonthKopecks: number | null;
    } | null;
    addons: Array<{
      id: string;
      code: string;
      name: string;
      /** Цена за единицу. */
      priceMonthKopecks: number;
      quantity: number;
      /** Не null — модуль «штучный» (доп. кассы/пользователи), количество до maxQuantity. */
      maxQuantity: number | null;
      activeFrom: string;
      activeUntil: string | null;
      /** false — отключён и работает до activeUntil, в следующее списание не входит. */
      renewing?: boolean;
    }>;
  } | null;
  /** Лимиты тарифа с докупленными модулями; null — подписки нет. */
  limits: { cashiers: LimitInfo; users: LimitInfo } | null;
  users: Array<{
    id: string;
    role: string;
    name: string | null;
    email: string | null;
    phone: string | null;
    status: string;
    lastLoginAt: string | null;
  }>;
  usage: {
    ordersLast30d: number;
    revenueLast30d: number;
    devicesOnline: number;
    devicesTotal: number;
  };
  devices: Array<{
    id: string;
    name: string;
    platform: DeviceKindUpper;
    lastSeenAt: string | null;
    lastIp: string | null;
    revoked: boolean;
    createdAt: string;
  }>;
  payments: Array<{
    id: string;
    amount: number;
    status: PaymentStatusUpper;
    method: string | null;
    externalId: string | null;
    failureReason: string | null;
    createdAt: string;
    paidAt: string | null;
  }>;
}

export interface TenantsQuery {
  q?: string;
  status?: SubscriptionStatusKey | 'all';
  /** all | none (без менеджера) | any | <managerId> */
  referrer?: string;
  access?: AccessFilterKey;
  limit?: number;
  offset?: number;
}

export const listTenants = (q: TenantsQuery = {}) => {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.status && q.status !== 'all') p.set('status', q.status);
  if (q.referrer && q.referrer !== 'all') p.set('referrer', q.referrer);
  if (q.access && q.access !== 'all') p.set('access', q.access);
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.offset !== undefined) p.set('offset', String(q.offset));
  const qs = p.toString();
  // accessCounts — сколько точек в каждом фильтре срока при текущих поиске/статусе/менеджере.
  return apiGet<{ results: TenantListItem[]; total: number; accessCounts: AccessCounts }>(
    `/tenants${qs ? `?${qs}` : ''}`,
  );
};

export const getTenant = (id: string) => apiGet<TenantDetail>(`/tenants/${id}`);

export const extendTenantTrial = (id: string, days: number) =>
  apiPost<{ ok: boolean; trialEndsAt: string | null }>(`/tenants/${id}/extend-trial`, { days });

export const setTenantStatus = (
  id: string,
  status: SubscriptionStatusUpper,
  reason?: string,
) =>
  apiPost<{ ok: boolean; status: SubscriptionStatusUpper }>(
    `/tenants/${id}/subscription-status`,
    { status, reason },
  );

// ─── Tenant: edit profile / trial / plan / addons ────────────────────

export interface UpdateTenantBody {
  name?: string;
  address?: string | null;
  phone?: string | null;
  domain?: string | null;
  timezone?: string;
  currency?: string;
  ownerName?: string;
  ownerPhone?: string | null;
  /** Вход владельца во всю сеть; смена завершает его сессии. */
  ownerEmail?: string;
}

export const updateTenant = (id: string, body: UpdateTenantBody) =>
  apiPatch<{
    ok: boolean;
    ownerEmailChanged: { from: string | null; to: string; establishments: number } | null;
  }>(`/tenants/${id}`, body);

// ─── Владелец: передать точку, завершить сессии ──────────────────────

export interface DetachOwnerBody {
  email: string;
  name?: string | null;
  phone?: string | null;
  /** Почта уже у владельца другой сети — точку отдаём в его сеть. */
  attachToExisting?: boolean;
  revokePreviousOwnerSessions?: boolean;
  clearSavedCard?: boolean;
  clearYukassa?: boolean;
}

export interface DetachOwnerResult {
  establishment: { id: string; name: string };
  newOwner: { id: string; email: string; mode: 'created' | 'promoted' | 'attached' };
  previousOwner: {
    id: string;
    name: string | null;
    email: string | null;
    remainingEstablishments: number;
    homeEstablishmentId: string | null;
    sessionsRevoked: boolean;
    pushRemoved: number;
  } | null;
  clearedSavedCard: boolean;
  clearedYukassa: boolean;
}

/** details ответа 409 OWNER_EXISTS. */
export interface ExistingOwnerInfo {
  owner: { id: string; name: string | null; email: string; establishments: number };
}

export const detachTenantOwner = (id: string, body: DetachOwnerBody) =>
  apiPost<DetachOwnerResult>(`/tenants/${id}/owner/detach`, body);

export const revokeTenantOwnerSessions = (id: string) =>
  apiPost<{
    owner: { id: string; name: string | null; email: string | null };
    revokedAt: string;
    pushRemoved: number;
  }>(`/tenants/${id}/owner/revoke-sessions`, {});

/**
 * НЕОБРАТИМО удаляет арендатора со всеми данными и пользователями.
 * Подтверждение (ввод точного названия) — ответственность UI.
 */
export const deleteTenant = (id: string) =>
  apiDelete<{ ok: boolean; name: string; deletedUsers: number }>(`/tenants/${id}`);

/**
 * Продлить/сдвинуть trial.
 *   { days: 14 } — прибавить 14 дней к текущей дате окончания.
 *   { until: ISO } — выставить конкретную дату окончания trial.
 */
export const adjustTenantTrial = (
  id: string,
  body: { days?: number; until?: string },
) =>
  apiPost<{ ok: boolean; trialEndsAt: string | null }>(
    `/tenants/${id}/trial`,
    body,
  );

/**
 * Уменьшить срок доступа (триал или оплаченный период).
 *   { days: 7 } — на 7 дней от текущего конца;
 *   { until: ISO } — до даты (раньше текущего конца).
 */
export const reduceTenantAccess = (id: string, body: { days?: number; until?: string }) =>
  apiPost<{
    ok: boolean;
    accessEndsAt: string | null;
    daysLeft: number | null;
    accessState: AccessState;
  }>(`/tenants/${id}/access/reduce`, body);

export interface ChangeTenantPlanBody {
  planCode: string;
  billingPeriod: 'MONTHLY' | 'YEARLY';
  /**
   * Историческое поле (надбавка за доп. точки на одной подписке). Тариф меняется
   * только у этой точки, сумма — за одно заведение; UI поле не отправляет.
   */
  additionalEstablishments?: number;
}

export const changeTenantPlan = (id: string, body: ChangeTenantPlanBody) =>
  apiPost<{
    ok: boolean;
    plan: string;
    billingPeriod: 'MONTHLY' | 'YEARLY';
    amount: number;
    additionalEstablishments: number;
  }>(`/tenants/${id}/plan`, body);

/**
 * Ручная активация оплаченного периода за наличку (вне ЮKassa): статус ACTIVE +
 * сдвиг currentPeriodEnd + платёж method='cash'. Срок задаётся РОВНО одним из
 * полей: period (календарный) | days (N дней) | until (ISO-дата).
 */
export interface ActivatePaidBody {
  period?: 'MONTHLY' | 'YEARLY';
  days?: number;
  until?: string;
  amountKopecks?: number;
  note?: string;
  /** false — перевести в ACTIVE без платежа в истории (партнёр, компенсация, тест). */
  recordPayment?: boolean;
}

export const activatePaidPeriod = (id: string, body: ActivatePaidBody) =>
  apiPost<{ ok: boolean; status: SubscriptionStatusUpper; currentPeriodEnd: string | null }>(
    `/tenants/${id}/activate-paid`,
    body,
  );

export interface AddonCatalogItem {
  code: string;
  name: string;
  description: string;
  priceMonthKopecks: number;
  /** Не null — модуль подключается штуками (доп. кассы/пользователи). */
  maxQuantity: number | null;
}

export const getAddonCatalog = () =>
  apiGet<{ items: AddonCatalogItem[] }>(`/tenants/addons/catalog`);

export const attachTenantAddon = (id: string, code: string, quantity?: number) =>
  apiPost<{
    ok: boolean;
    id: string;
    code: string;
    name: string;
    priceMonthKopecks: number;
    quantity: number;
  }>(`/tenants/${id}/addons`, { code, quantity });

export const setTenantAddonQuantity = (id: string, code: string, quantity: number) =>
  apiPatch<{ ok: boolean; code: string; quantity: number }>(
    `/tenants/${id}/addons/${encodeURIComponent(code)}`,
    { quantity },
  );

export const detachTenantAddon = (id: string, code: string) =>
  apiDelete<{ ok: boolean }>(`/tenants/${id}/addons/${encodeURIComponent(code)}`);

// ─── Billing ─────────────────────────────────────────────────────────

export interface BillingSummary {
  total: number;               // копейки, сумма всех SUCCEEDED за всё время
  failed: number;
  refunded: number;
  activeSubscriptions: number;
}

export interface PaymentItem {
  id: string;
  amount: number;     // копейки
  currency: string;
  status: PaymentStatusUpper;
  method: string | null;
  externalId: string | null;
  failureReason: string | null;
  refundedAmount: number;
  createdAt: string;
  paidAt: string | null;
  tenantId: string;
  tenantName: string;
}

export interface PaymentsQuery {
  status?: PaymentStatusUpper | 'all';
  tenantId?: string;
  limit?: number;
  offset?: number;
}

export const getBillingSummary = () => apiGet<BillingSummary>('/billing/summary');

export const listPayments = (q: PaymentsQuery = {}) => {
  const p = new URLSearchParams();
  if (q.status && q.status !== 'all') p.set('status', q.status);
  if (q.tenantId) p.set('tenantId', q.tenantId);
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.offset !== undefined) p.set('offset', String(q.offset));
  const qs = p.toString();
  return apiGet<{ results: PaymentItem[]; total: number }>(
    `/billing/payments${qs ? `?${qs}` : ''}`,
  );
};

// ─── Devices ─────────────────────────────────────────────────────────

export interface DeviceItem {
  id: string;
  name: string;
  platform: DeviceKindUpper;
  online: boolean;
  lastSeenAt: string | null;
  lastIp: string | null;
  revoked: boolean;
  revokedReason: string | null;
  createdAt: string;
  tenantId: string;
  tenantName: string;
}

export interface DevicesQuery {
  tenantId?: string;
  onlyActive?: boolean;
  limit?: number;
}

export const listDevices = (q: DevicesQuery = {}) => {
  const p = new URLSearchParams();
  if (q.tenantId) p.set('tenantId', q.tenantId);
  if (q.onlyActive !== undefined) p.set('onlyActive', String(q.onlyActive));
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  const qs = p.toString();
  return apiGet<{ results: DeviceItem[] }>(`/devices${qs ? `?${qs}` : ''}`);
};

export const revokeDevice = (id: string, reason?: string) =>
  apiPost<{ ok: boolean }>(`/devices/${id}/revoke`, { reason });

// ─── Support ─────────────────────────────────────────────────────────

export interface SupportTicketItem {
  id: string;
  subject: string;
  body: string;
  channel: TicketChannelUpper;
  status: TicketStatusUpper;
  from: {
    email: string | null;
    name: string | null;
    phone: string | null;
  };
  createdAt: string;
  lastMessageAt: string;
  resolvedAt: string | null;
  tenantId: string | null;
  tenantName: string | null;
}

export const listTickets = (status: 'all' | TicketStatusUpper = 'all', limit = 100) => {
  const p = new URLSearchParams();
  if (status !== 'all') p.set('status', status);
  p.set('limit', String(limit));
  return apiGet<{ results: SupportTicketItem[] }>(`/tickets?${p.toString()}`);
};

export const updateTicket = (
  id: string,
  data: { status?: TicketStatusUpper; assignedToId?: string | null },
) =>
  apiPatch<{ ok: boolean; status: TicketStatusUpper }>(`/tickets/${id}`, data);

// ─── Audit ───────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  meta: unknown;
  ip: string | null;
  createdAt: string;
  actor: { id: string; email: string | null; name: string | null } | null;
}

export interface AuditQuery {
  actorId?: string;
  entity?: string;
  limit?: number;
}

export const listAudit = (q: AuditQuery = {}) => {
  const p = new URLSearchParams();
  if (q.actorId) p.set('actorId', q.actorId);
  if (q.entity) p.set('entity', q.entity);
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  const qs = p.toString();
  return apiGet<{ results: AuditEntry[] }>(`/audit${qs ? `?${qs}` : ''}`);
};

// ─── Settings ────────────────────────────────────────────────────────

export interface PlatformSetting {
  key: string;
  value: unknown;
  isSecret: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export const listSettings = () =>
  apiGet<{ results: PlatformSetting[] }>('/settings');

export const upsertSetting = (key: string, value: unknown) =>
  apiPut<{ ok: boolean }>('/settings', { key, value });

export const deleteSetting = (key: string) =>
  apiDelete<{ ok: boolean }>(`/settings/${encodeURIComponent(key)}`);

export interface BulkSettingsEntry {
  key: string;
  value: unknown;
}

export interface BulkSettingsResult {
  ok: boolean;
  writtenKeys: string[];
  skippedMaskedKeys: string[];
}

/**
 * Массово сохраняет настройки одной секции. Секреты, которые не менялись,
 * можно НЕ включать в entries — backend всё равно пропускает ключи со значением
 * в виде маски (на случай ошибки клиента).
 */
export const bulkUpsertSettings = (
  section: string,
  entries: BulkSettingsEntry[],
) => apiPut<BulkSettingsResult>('/settings/bulk', { section, entries });

// ─── Plans ───────────────────────────────────────────────────────────
export interface PlanItem {
  id: string;
  code: string;
  name: string;
  tagline: string | null;
  description: string | null;
  priceMonthKopecks: number;
  priceYearKopecks: number;
  currency: string;
  maxEstablishments: number | null;
  maxCashiers: number | null;
  maxUsers: number | null;
  extraEstablishmentPriceMonthKopecks: number | null;
  features: string[];
  highlights: string[];
  isPublic: boolean;
  isPopular: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlanInput {
  code: string;
  name: string;
  tagline?: string | null;
  description?: string | null;
  priceMonthKopecks: number;
  priceYearKopecks: number;
  maxEstablishments?: number | null;
  maxCashiers?: number | null;
  maxUsers?: number | null;
  extraEstablishmentPriceMonthKopecks?: number | null;
  features: string[];
  highlights: string[];
  isPublic?: boolean;
  isPopular?: boolean;
  sortOrder?: number;
}

export const listPlans = () => apiGet<{ plans: PlanItem[] }>('/plans');
export const getPlan = (id: string) => apiGet<PlanItem>(`/plans/${id}`);
export const createPlan = (input: PlanInput) => apiPost<PlanItem>('/plans', input);
export const updatePlan = (id: string, input: Partial<PlanInput>) =>
  apiPatch<PlanItem>(`/plans/${id}`, input);
export const deletePlan = (id: string) => apiDelete<{ ok: boolean }>(`/plans/${id}`);

// ─── Suppliers (B2B-магазин «Закуп») ──────────────────────────────────

export type SupplierStatusKey = 'pending' | 'active' | 'blocked';
export type SupplierStatusUpper = 'PENDING' | 'ACTIVE' | 'BLOCKED';
export type SupplierSubStatusKey = 'trial' | 'active' | 'past_due' | 'cancelled';
export type SupplierSubStatusUpper = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';

export interface SupplierListItem {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  city: string | null;
  logoUrl: string | null;
  status: SupplierStatusKey;
  subscriptionStatus: SupplierSubStatusKey;
  inn: string | null;
  legalForm: string | null;
  legalName: string | null;
  productsCount: number;
  categoriesCount: number;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  /** Дата окончания доступа: триал или оплаченный период. */
  accessEndsAt: string | null;
  daysLeft: number | null;
  /** У поставщиков грейса нет: срок вышел → каталог сразу скрыт. */
  accessState: 'ok' | 'blocked';
  /** Итог модерации + срока: видят ли каталог заведения. */
  visibleInStore: boolean;
  autoRenew: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface SupplierPayment {
  id: string;
  amount: number; // копейки
  currency: string;
  status: PaymentStatusUpper;
  method: string | null;
  failureReason: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface SupplierDetail extends SupplierListItem {
  description: string | null;
  kpp: string | null;
  ogrn: string | null;
  taxSystem: string | null;
  legalAddress: string | null;
  warehouseAddress: string | null;
  contactPerson: string | null;
  bankAccount: string | null;
  bik: string | null;
  bankName: string | null;
  corrAccount: string | null;
  tags: string[];
  deliveryRegions: string[];
  deliverySchedule: string | null;
  minOrderAmount: number | null;   // копейки
  freeDeliveryFrom: number | null; // копейки
  defaultPaymentTerms: string;
  defaultDeferDays: number | null;
  updatedAt: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  priceKop: number;
  hasPaymentMethod: boolean;
  payments: SupplierPayment[];
}

/** Фильтр по подписке; 'expiring' — доступ кончается ≤7 дней или уже закрыт. */
export type SupplierSubFilter = SupplierSubStatusKey | 'all' | 'expiring';

export interface SuppliersQuery {
  q?: string;
  status?: SupplierStatusKey | 'all';
  sub?: SupplierSubFilter;
  limit?: number;
  offset?: number;
}

export const listSuppliers = (q: SuppliersQuery = {}) => {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.status && q.status !== 'all') p.set('status', q.status);
  if (q.sub && q.sub !== 'all') p.set('sub', q.sub);
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.offset !== undefined) p.set('offset', String(q.offset));
  const qs = p.toString();
  return apiGet<{ results: SupplierListItem[]; total: number }>(
    `/suppliers${qs ? `?${qs}` : ''}`,
  );
};

export const getSupplier = (id: string) => apiGet<SupplierDetail>(`/suppliers/${id}`);

export const setSupplierStatus = (
  id: string,
  status: SupplierStatusUpper,
  reason?: string,
) =>
  apiPost<{ ok: boolean; status: SupplierStatusUpper }>(
    `/suppliers/${id}/status`,
    { status, reason },
  );

/**
 * Продлить/сдвинуть пробный период поставщика.
 *   { days: 14 } — прибавить к текущей дате окончания;
 *   { until: ISO } — выставить конкретную дату.
 */
export const adjustSupplierTrial = (id: string, body: { days?: number; until?: string }) =>
  apiPost<{
    ok: boolean;
    subscriptionStatus: SupplierSubStatusUpper;
    trialEndsAt: string | null;
  }>(`/suppliers/${id}/trial`, body);

/**
 * Ручная активация оплаченного периода поставщика (наличные/перевод вне
 * ЮKassa). Срок задаётся РОВНО одним из: period | days | until.
 */
export interface ActivateSupplierPaidBody {
  period?: 'MONTHLY' | 'YEARLY';
  days?: number;
  until?: string;
  amountKopecks?: number;
  note?: string;
  /** false — перевести в ACTIVE без платежа в истории (партнёр, компенсация). */
  recordPayment?: boolean;
  /** true — заодно снять с модерации (status=ACTIVE), иначе витрина скрыта. */
  approve?: boolean;
}

export const activateSupplierPaid = (id: string, body: ActivateSupplierPaidBody) =>
  apiPost<{
    ok: boolean;
    status: SupplierStatusUpper;
    subscriptionStatus: SupplierSubStatusUpper;
    currentPeriodEnd: string | null;
  }>(`/suppliers/${id}/activate-paid`, body);

export const setSupplierSubscriptionStatus = (
  id: string,
  status: SupplierSubStatusUpper,
  reason?: string,
) =>
  apiPost<{ ok: boolean; subscriptionStatus: SupplierSubStatusUpper }>(
    `/suppliers/${id}/subscription-status`,
    { status, reason },
  );

/** Сброс пароля кабинета: новый пароль возвращается один раз. */
export const resetSupplierPassword = (id: string, password?: string) =>
  apiPost<{ ok: boolean; email: string; password: string }>(
    `/suppliers/${id}/reset-password`,
    password ? { password } : {},
  );

// ─── Supplier Finance (Фаза 2.5: подписки поставщиков) ────────────────

export interface SupplierFinancePayment {
  id: string;
  amount: number;   // копейки
  currency: string;
  status: PaymentStatusUpper;
  method: string | null;
  failureReason: string | null;
  createdAt: string;
  paidAt: string | null;
  supplierId: string;
  supplierName: string;
}

export interface SupplierUpcomingRenewal {
  id: string;
  name: string;
  currentPeriodEnd: string | null;
}

export interface SupplierFinance {
  priceKop: number;         // цена тарифа поставщика, копейки
  mrr: number;              // копейки (active × price)
  revenueTotal: number;     // копейки, все SUCCEEDED
  revenueLast30d: number;   // копейки
  succeededCount: number;
  failedCount: number;
  counts: {
    trial: number;
    active: number;
    pastDue: number;
    cancelled: number;
    total: number;
  };
  upcomingRenewals: SupplierUpcomingRenewal[];
  recentPayments: SupplierFinancePayment[];
}

export const getSupplierFinance = () =>
  apiGet<SupplierFinance>('/suppliers/finance');

// ─── Wholesalers (Resstro Оптовик — независимый модуль) ───────────────
// Та же форма API, что у Suppliers: модерация (status) отдельно от
// подписки (subscriptionStatus). У Оптовика нет публичной витрины —
// только «доступ к кабинету», поэтому visibleInStore не возвращается.

export type WholesalerStatusKey = 'pending' | 'active' | 'blocked';
export type WholesalerStatusUpper = 'PENDING' | 'ACTIVE' | 'BLOCKED';
export type WholesalerSubStatusKey = 'trial' | 'active' | 'past_due' | 'cancelled';
export type WholesalerSubStatusUpper = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';

export interface WholesalerListItem {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  city: string | null;
  logoUrl: string | null;
  status: WholesalerStatusKey;
  subscriptionStatus: WholesalerSubStatusKey;
  itemsCount: number;
  salesCount: number;
  trialEndsAt: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  accessEndsAt: string | null;
  daysLeft: number | null;
  accessState: 'ok' | 'blocked';
  autoRenew: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface WholesalerPayment {
  id: string;
  amount: number; // копейки
  currency: string;
  status: PaymentStatusUpper;
  method: string | null;
  failureReason: string | null;
  createdAt: string;
  paidAt: string | null;
}

export interface WholesalerDetail extends WholesalerListItem {
  updatedAt: string;
  cancelledAt: string | null;
  cancelReason: string | null;
  lifetimeRevenue: number; // копейки
  priceKop: number;
  hasPaymentMethod: boolean;
  payments: WholesalerPayment[];
}

export type WholesalerSubFilter = WholesalerSubStatusKey | 'all' | 'expiring';

export interface WholesalersQuery {
  q?: string;
  status?: WholesalerStatusKey | 'all';
  sub?: WholesalerSubFilter;
  limit?: number;
  offset?: number;
}

export const listWholesalers = (q: WholesalersQuery = {}) => {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.status && q.status !== 'all') p.set('status', q.status);
  if (q.sub && q.sub !== 'all') p.set('sub', q.sub);
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  if (q.offset !== undefined) p.set('offset', String(q.offset));
  const qs = p.toString();
  return apiGet<{ results: WholesalerListItem[]; total: number }>(
    `/wholesalers${qs ? `?${qs}` : ''}`,
  );
};

export const getWholesaler = (id: string) => apiGet<WholesalerDetail>(`/wholesalers/${id}`);

export const setWholesalerStatus = (
  id: string,
  status: WholesalerStatusUpper,
  reason?: string,
) =>
  apiPost<{ ok: boolean; status: WholesalerStatusUpper }>(
    `/wholesalers/${id}/status`,
    { status, reason },
  );

export const adjustWholesalerTrial = (id: string, body: { days?: number; until?: string }) =>
  apiPost<{
    ok: boolean;
    subscriptionStatus: WholesalerSubStatusUpper;
    trialEndsAt: string | null;
  }>(`/wholesalers/${id}/trial`, body);

export interface ActivateWholesalerPaidBody {
  period?: 'MONTHLY' | 'YEARLY';
  days?: number;
  until?: string;
  amountKopecks?: number;
  note?: string;
  recordPayment?: boolean;
  approve?: boolean;
}

export const activateWholesalerPaid = (id: string, body: ActivateWholesalerPaidBody) =>
  apiPost<{
    ok: boolean;
    status: WholesalerStatusUpper;
    subscriptionStatus: WholesalerSubStatusUpper;
    currentPeriodEnd: string | null;
  }>(`/wholesalers/${id}/activate-paid`, body);

export const setWholesalerSubscriptionStatus = (
  id: string,
  status: WholesalerSubStatusUpper,
  reason?: string,
) =>
  apiPost<{ ok: boolean; subscriptionStatus: WholesalerSubStatusUpper }>(
    `/wholesalers/${id}/subscription-status`,
    { status, reason },
  );

export const resetWholesalerPassword = (id: string, password?: string) =>
  apiPost<{ ok: boolean; email: string; password: string }>(
    `/wholesalers/${id}/reset-password`,
    password ? { password } : {},
  );

export interface WholesalerFinancePayment {
  id: string;
  amount: number;
  currency: string;
  status: PaymentStatusUpper;
  method: string | null;
  failureReason: string | null;
  createdAt: string;
  paidAt: string | null;
  wholesalerId: string;
  wholesalerName: string;
}

export interface WholesalerUpcomingRenewal {
  id: string;
  name: string;
  currentPeriodEnd: string | null;
}

export interface WholesalerFinance {
  priceKop: number;
  mrr: number;
  revenueTotal: number;
  revenueLast30d: number;
  succeededCount: number;
  failedCount: number;
  counts: {
    trial: number;
    active: number;
    pastDue: number;
    cancelled: number;
    total: number;
  };
  upcomingRenewals: WholesalerUpcomingRenewal[];
  recentPayments: WholesalerFinancePayment[];
}

export const getWholesalerFinance = () =>
  apiGet<WholesalerFinance>('/wholesalers/finance');

// ─── Витрина гостевого приложения (People) ───────────────────────────
// Баннеры и кухни на главной, кухни заведения, модерация отзывов.
// Любая правка сбрасывает кеш публичного /api/public/app/home — телефоны
// увидят изменения при следующем открытии (клиентский кеш 60 с).

export type BannerAction = 'NONE' | 'URL' | 'ESTABLISHMENT';

export interface AppBanner {
  id: string;
  title: string | null;
  subtitle: string | null;
  imageUrl: string;
  action: BannerAction;
  actionValue: string | null;
  bgColor: string | null;
  sortOrder: number;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppBannerInput {
  title?: string | null;
  subtitle?: string | null;
  imageUrl: string;
  action: BannerAction;
  actionValue?: string | null;
  bgColor?: string | null;
  sortOrder: number;
  isActive: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
}

export const listAppBanners = () => apiGet<{ banners: AppBanner[] }>('/app/banners');
export const createAppBanner = (input: AppBannerInput) => apiPost<AppBanner>('/app/banners', input);
export const updateAppBanner = (id: string, input: Partial<AppBannerInput>) =>
  apiPatch<AppBanner>(`/app/banners/${id}`, input);
export const deleteAppBanner = (id: string) => apiDelete<{ ok: boolean }>(`/app/banners/${id}`);

export interface AppCuisine {
  id: string;
  slug: string;
  name: string;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  establishmentsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AppCuisineInput {
  name: string;
  slug?: string;
  imageUrl?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export const listAppCuisines = () => apiGet<{ cuisines: AppCuisine[] }>('/app/cuisines');
export const createAppCuisine = (input: AppCuisineInput) =>
  apiPost<AppCuisine>('/app/cuisines', input);
export const updateAppCuisine = (id: string, input: Partial<AppCuisineInput>) =>
  apiPatch<AppCuisine>(`/app/cuisines/${id}`, input);
export const deleteAppCuisine = (id: string) => apiDelete<{ ok: boolean }>(`/app/cuisines/${id}`);

export const getTenantCuisines = (tenantId: string) =>
  apiGet<{ cuisineIds: string[] }>(`/tenants/${tenantId}/cuisines`);
export const setTenantCuisines = (tenantId: string, cuisineIds: string[]) =>
  apiPut<{ cuisineIds: string[] }>(`/tenants/${tenantId}/cuisines`, { cuisineIds });

export interface AppReviewItem {
  id: string;
  rating: number;
  text: string | null;
  authorName: string | null;
  isHidden: boolean;
  createdAt: string;
  establishment: { id: string; name: string };
  client: { id: string; name: string | null; email: string | null; phone: string | null };
  order: { id: string; number: number; total: number; completedAt: string | null } | null;
}

export interface AppReviewsQuery {
  establishmentId?: string;
  q?: string;
  hidden?: 'all' | 'visible' | 'hidden';
  limit?: number;
  offset?: number;
}

export const listAppReviews = (q: AppReviewsQuery = {}) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
  }
  const qs = params.toString();
  return apiGet<{ total: number; results: AppReviewItem[] }>(`/app/reviews${qs ? `?${qs}` : ''}`);
};
export const setAppReviewHidden = (id: string, isHidden: boolean) =>
  apiPatch<{ id: string; isHidden: boolean }>(`/app/reviews/${id}/hidden`, { isHidden });

// ─── Пользователи приложения People ─────────────────────────────────

export type PeopleUserStatus = 'ACTIVE' | 'BLOCKED' | 'DELETED';
export type PeopleUserSource = 'people' | 'menu';

export interface PeopleUserListItem {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  status: PeopleUserStatus;
  source: PeopleUserSource;
  establishment: { id: string; name: string } | null;
  hasPeopleApp: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  blockedAt: string | null;
  blockedReason: string | null;
  hasNote: boolean;
  counts: { orders: number; reservations: number; reviews: number; favorites: number };
}

export interface PeopleUsersQuery {
  q?: string;
  status?: 'all' | 'active' | 'blocked' | 'deleted';
  source?: 'all' | PeopleUserSource;
  sort?: 'created' | 'lastLogin' | 'orders';
  limit?: number;
  offset?: number;
}

export interface PeopleUserDetail {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  status: PeopleUserStatus;
  source: PeopleUserSource;
  hasPeopleApp: boolean;
  establishment: { id: string; name: string } | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  blockedAt: string | null;
  blockedReason: string | null;
  adminNote: string | null;
  stats: {
    orders: number;
    completedOrders: number;
    /** копейки */
    spentTotal: number;
    lastOrderAt: string | null;
    reservations: number;
    reviews: number;
    favorites: number;
  };
  addresses: {
    id: string;
    title: string;
    address: string;
    entrance: string | null;
    floor: string | null;
    apartment: string | null;
    comment: string | null;
    isDefault: boolean;
    createdAt: string;
  }[];
  favorites: { establishment: { id: string; name: string; showInApp: boolean }; createdAt: string }[];
  devices: {
    id: string;
    platform: 'IOS' | 'ANDROID' | 'WEB';
    appBundleId: string | null;
    deviceModel: string | null;
    appVersion: string | null;
    locale: string | null;
    lastUsedAt: string;
    createdAt: string;
  }[];
  orders: {
    id: string;
    number: number;
    status: 'NEW' | 'IN_PROGRESS' | 'READY' | 'COMPLETED' | 'CANCELLED';
    source: 'CASH' | 'APP';
    total: number;
    paidFromApp: boolean;
    deliveryAddress: string | null;
    createdAt: string;
    completedAt: string | null;
    establishment: { id: string; name: string };
  }[];
  reservations: {
    id: string;
    guests: number;
    startsAt: string;
    status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
    comment: string | null;
    createdAt: string;
    establishment: { id: string; name: string };
  }[];
  reviews: {
    id: string;
    rating: number;
    text: string | null;
    isHidden: boolean;
    createdAt: string;
    establishment: { id: string; name: string };
  }[];
  audit: {
    id: string;
    action: string;
    meta: Record<string, unknown>;
    createdAt: string;
    operator: { id: string; name: string | null; email: string | null } | null;
  }[];
}

export interface PeopleUserPatch {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  adminNote?: string | null;
}

export const listPeopleUsers = (q: PeopleUsersQuery = {}) => {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '' && v !== null) params.set(k, String(v));
  }
  const qs = params.toString();
  return apiGet<{ total: number; results: PeopleUserListItem[] }>(`/app/users${qs ? `?${qs}` : ''}`);
};
export const getPeopleUser = (id: string) => apiGet<PeopleUserDetail>(`/app/users/${id}`);
export const updatePeopleUser = (id: string, patch: PeopleUserPatch) =>
  apiPatch<{ id: string; name: string | null; phone: string | null; email: string | null; adminNote: string | null }>(
    `/app/users/${id}`,
    patch,
  );
export const setPeopleUserStatus = (id: string, status: 'ACTIVE' | 'BLOCKED', reason?: string) =>
  apiPost<{ ok: boolean; status: PeopleUserStatus; blockedAt?: string | null; blockedReason?: string | null }>(
    `/app/users/${id}/status`,
    { status, reason: reason?.trim() || undefined },
  );
export const removePeopleUserDevices = (id: string, subscriptionId?: string) =>
  apiDelete<{ ok: boolean; removed: number }>(
    `/app/users/${id}/devices${subscriptionId ? `?subscriptionId=${encodeURIComponent(subscriptionId)}` : ''}`,
  );

/** Загрузка картинки витрины. Ответ бэкенда — { url } относительный /uploads/… */
export async function uploadAppImage(kind: 'banner' | 'cuisine', file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return api<{ url: string }>(`/app/uploads/${kind}`, { method: 'POST', body: fd });
}

/**
 * Абсолютный URL картинки для <img>. Локальное хранилище отдаёт
 * относительные `/uploads/…` — в dev их проксирует vite, а в проде sadmin и
 * API живут на разных доменах, поэтому подставляем VITE_API_URL. S3-ссылки
 * (https://…) возвращаем как есть.
 */
export function mediaUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const origin = (import.meta.env.VITE_API_URL?.trim() || '').replace(/\/+$/, '');
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

// ─── Менеджеры (реферальная программа) ─────────────────────────────

/**
 * HEAD_MANAGER — главный: все заведения, менеджеры, наличные и продление сроков.
 * SALES_DIRECTOR — руководитель отдела продаж: права главного плюс оборот компании,
 * сейф отдела, расходы и рейтинг менеджеров.
 */
export type ManagerRole = 'MANAGER' | 'HEAD_MANAGER' | 'SALES_DIRECTOR';
export type ManagerRoleFilter = 'all' | 'director' | 'head' | 'manager';

export interface ManagerListItem {
  id: string;
  email: string;
  name: string;
  code: string;
  commissionBps: number;
  status: 'active' | 'blocked';
  role: ManagerRole;
  /** Главный менеджер, который завёл этого менеджера из своего кабинета. */
  createdByManager: { id: string; name: string } | null;
  establishments: number;
  pendingKopecks: number;
  lastLoginAt: string | null;
  createdAt: string;
  /** Удалён руководителем отдела из кабинета (status при этом blocked). */
  deletedAt?: string | null;
  referralLink: string;
}

export interface ManagerDetail {
  id: string;
  email: string;
  name: string;
  code: string;
  commissionBps: number;
  status: 'active' | 'blocked';
  role: ManagerRole;
  createdByManager: { id: string; name: string } | null;
  pendingKopecks: number;
  lastLoginAt: string | null;
  createdAt: string;
  referralLink: string;
  phone: string | null;
  note: string | null;
  updatedAt: string;
  deletedAt?: string | null;
  /** Кто удалил и куда ушли заведения — из журнала кабинета. */
  deletion?: {
    by: { id: string; name: string } | null;
    establishments: number | null;
    transferTo: { name: string } | null;
  } | null;
  earnedKopecks: number;
  establishments: Array<{
    id: string;
    name: string;
    city: string | null;
    attachedAt: string | null;
    commissionBps: number | null;
    source: string | null;
    subscription: { status: string; plan: string; amountKopecks: number } | null;
  }>;
  payouts: Array<{
    id: string;
    amountKopecks: number;
    method: string;
    periodFrom: string;
    periodTo: string;
    comment: string | null;
    createdAt: string;
    accrualsCount: number;
  }>;
}

export const listManagers = (
  q: { q?: string; status?: string; role?: ManagerRoleFilter; limit?: number; offset?: number } = {},
) => {
  const p = new URLSearchParams();
  if (q.q) p.set('q', q.q);
  if (q.status) p.set('status', q.status);
  if (q.role && q.role !== 'all') p.set('role', q.role);
  if (q.limit != null) p.set('limit', String(q.limit));
  if (q.offset != null) p.set('offset', String(q.offset));
  const qs = p.toString();
  return apiGet<{ total: number; items: ManagerListItem[] }>(`/managers${qs ? `?${qs}` : ''}`);
};

export const getManager = (id: string) => apiGet<ManagerDetail>(`/managers/${id}`);

export const createManager = (body: {
  email: string;
  name: string;
  phone?: string | null;
  code: string;
  commissionBps?: number;
  note?: string | null;
}) => apiPost<{ id: string; code: string }>('/managers', body);

export const updateManager = (
  id: string,
  body: Partial<{
    name: string;
    phone: string | null;
    commissionBps: number;
    note: string | null;
    status: 'ACTIVE' | 'BLOCKED';
    role: ManagerRole;
  }>,
) => apiPatch<{ ok: boolean }>(`/managers/${id}`, body);

/** Сейф отдела продаж: настройки (PlatformSetting sales.*) и остаток с даты учёта. */
export interface SalesSafeSummary {
  percent: number;
  /** YYYY-MM-DD по Москве. */
  startDate: string;
  inflowKopecks: number;
  expensesKopecks: number;
  payoutsKopecks: number;
  balanceKopecks: number;
  thisMonthInKopecks: number;
}

export const getSalesSafe = () => apiGet<SalesSafeSummary>('/sales/safe');

/** Запись журнала действий менеджера в его кабинете. */
export interface ManagerAuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId: string | null;
  meta: Record<string, unknown> | null;
  ip: string | null;
  /** Роль на момент действия — по ней работает фильтр «главные / обычные». */
  actorRole: ManagerRole;
  createdAt: string;
  manager: { id: string; name: string; email: string; code: string; role: ManagerRole } | null;
}

export interface ManagerAuditPage {
  items: ManagerAuditEntry[];
  nextCursor: string | null;
  /** director — с бэкенда этапа 32; до выката его нет. */
  counts: { all: number; director?: number; head: number; manager: number };
}

export const listManagerAudit = (
  q: {
    role?: ManagerRoleFilter;
    managerId?: string;
    action?: string;
    order?: 'desc' | 'asc';
    limit?: number;
    cursor?: string;
  } = {},
) => {
  const p = new URLSearchParams();
  if (q.role && q.role !== 'all') p.set('role', q.role);
  if (q.managerId) p.set('managerId', q.managerId);
  if (q.action) p.set('action', q.action);
  if (q.order) p.set('order', q.order);
  if (q.limit != null) p.set('limit', String(q.limit));
  if (q.cursor) p.set('cursor', q.cursor);
  const qs = p.toString();
  return apiGet<ManagerAuditPage>(`/managers/audit${qs ? `?${qs}` : ''}`);
};

export const createManagerPayout = (
  id: string,
  body: {
    method: 'cash' | 'transfer' | 'other';
    amountKopecks?: number;
    comment?: string | null;
  },
) => apiPost<{ id: string; amountKopecks: number; method: string; accrualsCount: number }>(
  `/managers/${id}/payouts`,
  body,
);

export const assignTenantReferrer = (tenantId: string, managerId: string | null) =>
  apiPatch<{ ok: boolean }>(`/tenants/${tenantId}/referrer`, { managerId });

export const bulkAssignTenantReferrer = (establishmentIds: string[], managerId: string | null) =>
  apiPost<{ ok: boolean; changed: number; skipped: number; total: number }>(
    '/tenants/referrer/bulk',
    { establishmentIds, managerId },
  );

// ─── Me ──────────────────────────────────────────────────────────────

export const getMe = () =>
  apiGet<{
    id: string;
    email: string;
    name: string;
    role: string;
    lastLoginAt: string | null;
  }>('/me');

export { api };
